import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';

import { AlbumForPpdPurchase } from '../../graphql/queries/albums';
import { getIdentityMergedMatchedPhotos } from '../../util/purchase-album-photos';
import {
  MeWithIdentities,
  MeQuery,
  MeForAlbumPurchase,
  MeForPpdPurchasePerIdentity,
  MeForAlbumPurchaseIdentities,
  MeWithPurchasedAlbumPrintsByAlbum,
} from '../../graphql/queries/account';
import { openModal } from '../../actions/modal-actions';
import PurchasePpdPhotos from '../../components/purchase/PurchasePpdPhotos';
import { selectIdentity as selectIdentityAction } from '../../actions/ppd-purchase';
import { getAlbumCheckoutItems } from '../../util/ppd-purchase';
import { checkoutPrice } from '../../graphql/queries/checkout';
import { getAlbumMembership, getPurchasedIdentities } from '../../util/account';
import withPurchasePpdCartItemToggle from './withPurchasePpdCartItemToggle';
import { ORDER_STATUS } from '../../constants/pro-custom-prints';

const withMe = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    userId: me?.id,
  }),
});

const withUserIdentities = graphql(MeWithIdentities, {
  props: ({ data: { me } }) => ({
    identities: me?.linked_identities?.records ?? [],
  }),
  // Skip fetching identities if the album does not have a folder
  // subscription since we won't need to go to folder sub checkout
  skip: ownProps => !ownProps.hasFolderSubscription,
});

const withMeForAlbumPurchase = graphql(MeForAlbumPurchase, {
  options: ({ match }) => ({
    variables: {
      albumId: match.params.albumId,
    },
  }),
  props: ({ data: { me } }) => ({
    user: me,
  }),
});

const withCheckoutPrice = graphql(checkoutPrice, {
  options: ({ cart, userId, album }) => {
    const { ppaItems, customPrintItems, photoItems } = getAlbumCheckoutItems(
      cart,
      album,
    );

    return {
      variables: {
        account_id: userId,
        subscription_items: ppaItems,
        custom_print_items: customPrintItems,
        photo_items: photoItems,
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    };
  },
  props: ({ data: { checkoutPrice: price, loading } = {} }) => ({
    price,
    loadingPrice: loading,
  }),
  skip: ({ userId, cart, album }) =>
    !cart || !userId || !album || cart.length === 0,
});

const withAlbumForPpdPurchase = graphql(AlbumForPpdPurchase, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { album, loading } }) => ({
    album,
    loading,
  }),
});

const withMatchedPhotosFromUser = graphql(MeForPpdPurchasePerIdentity, {
  options: ({ match }) => ({
    variables: {
      albumId: match.params.albumId,
    },
  }),
  props: ({ ownProps, data: { me, loading } }) => {
    const { match } = ownProps;
    const albumMembership = getAlbumMembership(me, match.params.albumId);

    if (!albumMembership) {
      return {
        loading,
      };
    }

    const selectedIdentities =
      albumMembership?.selected_identities?.records ?? [];
    const currentIdentity = selectedIdentities.find(
      selectedIdentity => selectedIdentity.id === ownProps?.activeIdentity?.id,
    );

    return {
      loading,
      selectedIdentities,
      matchedPhotos: getIdentityMergedMatchedPhotos(currentIdentity),
      entitledPhotosNo: albumMembership?.entitled_matched_photos?.total ?? 0,
      entitledPhotos: albumMembership?.entitled_matched_photos?.records ?? [],
    };
  },
});

const withUserData = graphql(MeForAlbumPurchaseIdentities, {
  options: ({ match }) => ({
    variables: {
      albumId: match.params.albumId,
    },
  }),
  props: ({ data: { me, loading }, ownProps: { match } }) => ({
    loading,
    purchasedIdentities: getPurchasedIdentities(me, match.params.albumId),
  }),
});

// TODO: This query should optimally not be here and the purchased
// album custom prints fetched from the `MeForAlbumPurchase`, however
// there is a breaking error in `WithPurchasePpdCartItemToggle`
// where the `AlbumMembershipWithPhotoTagPrice` query expects an
// AlbumMembership id param from the `MeForAlbumPurchase` query.
// If the latter query fails to resolve fast the `AlbumMembershipWithPhotoTagPrice` fails completely.
const withCustomerInputPurchasedCustomPrints = graphql(
  MeWithPurchasedAlbumPrintsByAlbum,
  {
    options: ({ match }) => ({
      variables: {
        albumId: match.params.albumId,
        orderStatuses: [ORDER_STATUS.CUSTOMER_INPUT],
      },
    }),
    props: ({ data: { me } }) => ({
      pendingPurchasedCustomPrints: me?.purchased_custom_prints?.records ?? [],
    }),
  },
);

const mapStateToProps = ({ ppdPurchase }) => ({
  cart: ppdPurchase.cart,
  checkoutSuccess: ppdPurchase.success,
  activeIdentity: ppdPurchase.selectedIdentity,
});

const mapDispatchToProps = {
  openModal,
  selectIdentity: selectIdentityAction,
};

export default compose(
  withPurchasePpdCartItemToggle,
  withRouter,
  withUserIdentities,
  withAlbumForPpdPurchase,
  connect(mapStateToProps, mapDispatchToProps),
  withMe,
  withMatchedPhotosFromUser,
  withMeForAlbumPurchase,
  withCustomerInputPurchasedCustomPrints,
  withUserData,
  withCheckoutPrice,
)(PurchasePpdPhotos);
